import { useState, useEffect } from "react";
import ActiveIcon from "../../Images/Active.svg";
import completedIcon from "../../Images/Completed.svg";
import QuizIcon from "../../Images/Quiz.svg";
import { API_URL } from "../../config/api";

export default function Quiz() {
  const [selectedBatch, setSelectedBatch] = useState("");
  const [selectedCourse, setSelectedCourse] = useState("");
  const [selectedModule, setSelectedModule] = useState("");
  const [selectedLesson, setSelectedLesson] = useState("");
  const [apiData, setApiData] = useState(null);
  const [allBatchesData, setAllBatchesData] = useState([]); // Store all batches
  const [quizData, setQuizData] = useState(null);
  const [attemptsData, setAttemptsData] = useState({});
  const [loading, setLoading] = useState(true);
  const [quizLoading, setQuizLoading] = useState(false);

  // Fetch default quiz context
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token =
          localStorage.getItem("token") || localStorage.getItem("access_token");
        const response = await fetch(`${API_URL}/assignments/default_context`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        console.log("Quiz default API Response:", result);

        // ✅ FIXED: API returns { data: [...] }
        if (
          result.data &&
          Array.isArray(result.data) &&
          result.data.length > 0
        ) {
          setAllBatchesData(result.data); // Store all batches

          // Set first batch as default
          const firstBatch = result.data[0];
          setApiData(firstBatch);

          // Set default selections from first batch
          if (firstBatch.schedule) {
            setSelectedBatch(firstBatch.schedule.schedule_id.toString());
          }
          if (firstBatch.course) {
            setSelectedCourse(firstBatch.course.course_id.toString());
          }
          if (firstBatch.modules && firstBatch.modules.length > 0) {
            setSelectedModule(firstBatch.modules[0].module_id.toString());
            if (
              firstBatch.modules[0].lessons &&
              firstBatch.modules[0].lessons.length > 0
            ) {
              const firstLessonId =
                firstBatch.modules[0].lessons[0].lesson_id.toString();
              setSelectedLesson(firstLessonId);
            }
          }
        }

        setLoading(false);
      } catch (error) {
        console.error("Error fetching quiz data:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Handle batch change
  const handleBatchChange = (scheduleId) => {
    setSelectedBatch(scheduleId);

    // Find the selected batch data
    const selectedBatchData = allBatchesData.find(
      (batch) => batch.schedule.schedule_id.toString() === scheduleId,
    );

    if (selectedBatchData) {
      setApiData(selectedBatchData);

      // Update course
      if (selectedBatchData.course) {
        setSelectedCourse(selectedBatchData.course.course_id.toString());
      }

      // Reset module and lesson
      if (selectedBatchData.modules && selectedBatchData.modules.length > 0) {
        setSelectedModule(selectedBatchData.modules[0].module_id.toString());
        if (
          selectedBatchData.modules[0].lessons &&
          selectedBatchData.modules[0].lessons.length > 0
        ) {
          setSelectedLesson(
            selectedBatchData.modules[0].lessons[0].lesson_id.toString(),
          );
        } else {
          setSelectedLesson("");
        }
      } else {
        setSelectedModule("");
        setSelectedLesson("");
      }
    }
  };

  // Fetch quiz details when lesson changes
  useEffect(() => {
    const fetchQuizDetails = async () => {
      if (!selectedLesson) {
        setQuizData(null);
        return;
      }

      setQuizLoading(true);
      try {
        const token =
          localStorage.getItem("token") || localStorage.getItem("access_token");
        // Use the new endpoint with lesson_id and schedule_id
        const response = await fetch(
          `${API_URL}/quiz/lesson/${selectedLesson}/schedule/${selectedBatch}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        console.log("Quiz Response:", result);

        // The new API returns an array in result.data, get the first item if exists
        if (result.success && result.data && result.data.length > 0) {
          const quizWithSchedule = result.data[0];
          setQuizData(quizWithSchedule);
          await fetchAttempts(quizWithSchedule.quiz_id);
        } else {
          setQuizData(null);
          setAttemptsData({});
        }

        setQuizLoading(false);
      } catch (error) {
        console.error("Error fetching quiz:", error);
        setQuizData(null);
        setAttemptsData({});
        setQuizLoading(false);
      }
    };

    fetchQuizDetails();
  }, [selectedLesson, selectedBatch]);

  // Fetch quiz attempts
  const fetchAttempts = async (quizId) => {
    try {
      const token =
        localStorage.getItem("token") || localStorage.getItem("access_token");
      const response = await fetch(`${API_URL}/quiz/attempts/${quizId}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        console.log("No attempts found or error fetching");
        setAttemptsData({});
        return;
      }

      const result = await response.json();
      console.log("Quiz Attempts Response:", result);

      if (result.success && result.data) {
        const attemptsMap = {};
        if (Array.isArray(result.data)) {
          result.data.forEach((attempt) => {
            attemptsMap[attempt.enrollment_id] = attempt;
          });
        } else {
          attemptsMap[result.data.enrollment_id] = result.data;
        }
        setAttemptsData(attemptsMap);
      }
    } catch (error) {
      console.error("Error fetching quiz attempts:", error);
      setAttemptsData({});
    }
  };

  const totalQuestions = quizData?.questions?.length || 0;

  // Get selected module's lessons
  const getSelectedModuleLessons = () => {
    if (!apiData?.modules || !selectedModule) return [];
    const module = apiData.modules.find(
      (m) => m.module_id.toString() === selectedModule,
    );
    return module?.lessons || [];
  };

  // Get students from API data
  const students = apiData?.students || [];

  // Calculate submission statistics
  const totalStudents = students.length;
  const submittedCount = Object.keys(attemptsData).length;
  const pendingCount = students.length - submittedCount;
  const average =
    totalStudents > 0 ? Math.round((submittedCount / totalStudents) * 100) : 0;

  // ✅ Quiz stats from backend (production safe)
  const quizStats = apiData?.course?.quizzes ?? {};

  const totalQuizzes = quizStats.total_course ?? 0;
  const activeQuizzes = quizStats.active ?? 0;
  const completedQuizzes = quizStats.completed ?? 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4AACC5] mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-5 bg-gray-50 min-h-screen">
      {/* Stats Cards - FULLY DYNAMIC */}
      <div className="flex justify-start items-start gap-3.5 mb-6">
        <div className="w-56 h-28 bg-white rounded shadow-sm p-4 flex items-center gap-4">
          <div className="w-16 h-16 bg-cyan-100 rounded-lg flex items-center justify-center">
            <img src={QuizIcon} alt="QuizIcon" />
          </div>
          <div className="flex flex-col">
            <div className="text-xs text-stone-900 font-['Roboto']">
              Total Quiz
            </div>
            <div className="text-2xl font-bold text-black font-['Inter']">
              {String(totalQuizzes).padStart(2, "0")}
            </div>
          </div>
        </div>

        <div className="w-56 h-28 bg-white rounded shadow-sm p-4 flex items-center gap-4">
          <div className="w-16 h-16 bg-cyan-100 rounded-lg flex items-center justify-center">
            <img src={completedIcon} alt="completedIcon" />
          </div>
          <div className="flex flex-col">
            <div className="text-xs text-stone-900 font-['Roboto']">
              Completed Quiz
            </div>
            <div className="text-2xl font-bold text-black font-['Inter']">
              {String(completedQuizzes).padStart(2, "0")}
            </div>
          </div>
        </div>

        <div className="w-56 h-28 bg-white rounded shadow-sm p-4 flex items-center gap-4">
          <div className="w-16 h-16 bg-cyan-100 rounded-lg flex items-center justify-center">
            <img src={ActiveIcon} alt="ActiveIcon" />
          </div>
          <div className="flex flex-col">
            <div className="text-xs text-stone-900 font-['Roboto']">
              Active Quiz
            </div>
            <div className="text-2xl font-bold text-black font-['Inter']">
              {String(activeQuizzes).padStart(2, "0")}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex gap-4 mb-6">
        {/* Left Panel - Batch Info */}
        <div className="w-[500px] bg-white rounded border border-gray-200 p-6 relative">
          <div className="absolute top-3.5 right-3.5">
            <div className="px-4 py-1 bg-[#D3F3FF] rounded-full">
              <div className="text-[#4AACC5] text-xs font-['Roboto']">
                Active
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3.5 mt-1">
            {/* Select Batch - FIXED */}
            <div className="flex flex-col items-start">
              <div className="text-stone-900 text-sm font-['Roboto'] mb-1">
                Select Batch
              </div>
              <select
                value={selectedBatch}
                onChange={(e) => handleBatchChange(e.target.value)}
                className="w-full text-left text-zinc-800 text-sm font-semibold font-['Roboto'] border-none outline-none bg-transparent cursor-pointer"
              >
                <option value="">Select Batch</option>

                {allBatchesData.map((batch) => (
                  <option
                    key={batch.schedule.schedule_id}
                    value={batch.schedule.schedule_id}
                  >
                    {batch.schedule.location} - {batch.course.title}
                  </option>
                ))}
              </select>
            </div>
            <hr className="border-stone-300" />

            {/* Program */}
            <div className="flex flex-col items-start">
              <div className="text-stone-900 text-sm font-['Roboto'] mb-1">
                Program
              </div>
              <select
                value={selectedCourse}
                onChange={(e) => setSelectedCourse(e.target.value)}
                className="w-full text-left text-zinc-800 text-sm font-semibold font-['Roboto'] border-none outline-none bg-transparent cursor-pointer"
              >
                <option value="">Select Course</option>
                {apiData?.course && (
                  <option value={apiData.course.course_id}>
                    {apiData.course.title}
                  </option>
                )}
              </select>
            </div>
            <hr className="border-stone-300" />

            {/* Module */}
            <div className="text-left">
              <div className="text-stone-900 text-sm font-['Roboto'] mb-1">
                Module
              </div>
              <select
                value={selectedModule}
                onChange={(e) => {
                  setSelectedModule(e.target.value);
                  const module = apiData?.modules?.find(
                    (m) => m.module_id.toString() === e.target.value,
                  );
                  if (module?.lessons && module.lessons.length > 0) {
                    setSelectedLesson(module.lessons[0].lesson_id.toString());
                  } else {
                    setSelectedLesson("");
                  }
                }}
                className="w-full text-left text-zinc-800 text-sm font-semibold font-['Roboto'] border-none outline-none bg-transparent cursor-pointer"
              >
                <option value="">Select Module</option>
                {apiData?.modules?.map((module) => (
                  <option key={module.module_id} value={module.module_id}>
                    {module.title}
                  </option>
                ))}
              </select>
            </div>
            <hr className="border-stone-300" />

            {/* Lesson */}
            <div className="text-left">
              <div className="text-stone-900 text-sm font-['Roboto'] mb-1">
                Lesson
              </div>
              <select
                value={selectedLesson}
                onChange={(e) => setSelectedLesson(e.target.value)}
                className="w-full text-left text-zinc-800 text-sm font-semibold font-['Roboto'] border-none outline-none bg-transparent cursor-pointer"
                disabled={!selectedModule}
              >
                <option value="">Select Lesson</option>
                {getSelectedModuleLessons().map((lesson) => (
                  <option key={lesson.lesson_id} value={lesson.lesson_id}>
                    {lesson.title}
                  </option>
                ))}
              </select>
            </div>
            <hr className="border-stone-300" />
          </div>
        </div>

        {/* Right Panel - Quiz Details */}
        <div className="flex-1 bg-white rounded border border-gray-200 p-6">
          {quizLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-zinc-600">Loading quiz...</div>
            </div>
          ) : quizData ? (
            <>
              <div className="flex justify-between items-start mb-4">
                <div className="text-xl text-zinc-800 font-semibold font-['Roboto']">
                  Active Quiz Details
                </div>
                <div>
                  <span className="text-base text-stone-900 font-['Roboto']">
                    End Date:{" "}
                  </span>
                  <span className="text-lg text-zinc-800 font-semibold font-['Roboto']">
                    {new Date(
                      quizData.QuizSchedule?.end_date || quizData.created_at,
                    ).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "2-digit",
                      day: "2-digit",
                    })}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-4 mb-4">
                <div className="text-sm text-zinc-800 font-bold font-['Roboto']">
                  {quizData.title}
                </div>
                {quizData.quiz_url && (
                  <a
                    href={quizData.quiz_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-cyan-500 font-medium font-['Roboto'] underline"
                  >
                    View Quiz
                  </a>
                )}
              </div>

              {/* Quiz Details Box */}
              <div className="bg-white rounded shadow border border-gray-200 p-4">
                <div className="flex gap-8">
                  {/* Left Section - Details */}
                  <div className="flex-1">
                    <div className="text-lg text-zinc-800 font-semibold font-['Roboto'] mb-4">
                      Details
                    </div>

                    <div className="mb-4">
                      <span className="text-sm text-zinc-600 font-['Roboto']">
                        Quiz Points:{" "}
                      </span>
                      <span className="text-sm text-stone-900 font-semibold font-['Roboto']">
                        {quizData.total_points}
                      </span>
                    </div>

                    <div className="mb-4">
                      <span className="text-sm text-zinc-600 font-['Roboto']">
                        Quiz Minimum Points:{" "}
                      </span>
                      <span className="text-sm text-stone-900 font-bold font-['Roboto']">
                        {quizData.passing_score}
                      </span>
                    </div>

                    <div className="mb-4">
                      <span className="text-sm text-zinc-600 font-['Roboto']">
                        Number of Questions:{" "}
                      </span>
                      <span className="text-sm text-stone-900 font-semibold font-['Roboto']">
                        {totalQuestions}
                      </span>
                    </div>

                    <div>
                      <span className="text-sm text-zinc-600 font-['Roboto']">
                        Overall Estimate Time:{" "}
                      </span>
                      <span className="text-sm text-stone-900 font-bold font-['Roboto']">
                        {quizData.time_limit_seconds || 0} seconds
                      </span>
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="w-px bg-gray-200"></div>

                  {/* Right Section - Student Can See */}
                  <div className="w-48">
                    <div className="text-lg text-zinc-800 font-semibold font-['Roboto'] mb-4">
                      Student Can See
                    </div>

                    <div className="text-sm text-zinc-600 font-['Roboto'] mb-3">
                      Missed Questions
                    </div>
                    <div className="text-sm text-zinc-600 font-['Roboto'] mb-3">
                      Correct Answers
                    </div>
                    <div className="text-sm text-zinc-600 font-['Roboto']">
                      Point Value
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-64">
              <div className="text-zinc-600">No quiz found for this lesson</div>
            </div>
          )}
        </div>
      </div>

      {/* Student Submission Status */}
      <div className="bg-white rounded border border-gray-200 p-6 mb-6">
        <div className="flex justify-between items-start mb-6">
          <div className="text-lg text-zinc-800 font-semibold font-['Roboto']">
            Student Submission Status
          </div>

          <div className="flex gap-8">
            <div className="text-center">
              <div className="text-2xl text-zinc-800 font-semibold font-['Roboto']">
                {totalStudents}
              </div>
              <div className="text-sm text-zinc-800 font-['Roboto']">
                Total Student
              </div>
            </div>

            <div className="text-center">
              <div className="text-2xl text-zinc-800 font-semibold font-['Roboto']">
                {submittedCount}
              </div>
              <div className="text-sm text-zinc-800 font-['Roboto']">
                Submitted
              </div>
            </div>

            <div className="text-center">
              <div className="text-2xl text-zinc-800 font-semibold font-['Roboto']">
                {pendingCount}
              </div>
              <div className="text-sm text-zinc-800 font-['Roboto']">
                Over Due
              </div>
            </div>

            <div className="text-center">
              <div className="text-2xl text-zinc-800 font-semibold font-['Roboto']">
                {average}%
              </div>
              <div className="text-sm text-zinc-800 font-['Roboto']">
                Average
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <button className="px-6 py-2 rounded border border-[#4AACC5] text-[#4AACC5] text-sm font-semibold font-['Roboto']">
                Remainder to Pending Students
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Student List Table */}
      <div className="bg-white rounded border border-gray-200 p-6">
        <div className="flex justify-between items-center mb-4">
          <div className="text-lg text-zinc-800 font-semibold font-['Roboto']">
            Student List
          </div>
          <button className="px-4 py-1.5 bg-white rounded border border-slate-200 flex items-center gap-2">
            <span className="text-sm text-zinc-800 font-medium font-['Roboto']">
              Filters
            </span>
            <svg className="w-3 h-2" fill="currentColor" viewBox="0 0 12 8">
              <path d="M10.293.293a1 1 0 011.414 1.414l-5 5a1 1 0 01-1.414 0l-5-5A1 1 0 011.707.293L6 4.586 10.293.293z" />
            </svg>
          </button>
        </div>

        <div className="overflow-x-auto">
          {students.length > 0 ? (
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-blue-50">
                  <th className="px-4 py-2.5 text-left text-xs text-gray-700 font-semibold font-['Roboto'] border border-slate-200">
                    Student Name ↕
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs text-gray-700 font-semibold font-['Roboto'] border border-slate-200">
                    Student ID ↕
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs text-gray-700 font-semibold font-['Roboto'] border border-slate-200">
                    Submitted On ↕
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs text-gray-700 font-semibold font-['Roboto'] border border-slate-200">
                    Status ↕
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs text-gray-700 font-semibold font-['Roboto'] border border-slate-200">
                    Score (Max {quizData?.total_points}) ↕
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs text-gray-700 font-semibold font-['Roboto'] border border-slate-200">
                    Result ↕
                  </th>
                </tr>
              </thead>
              <tbody>
                {students.map((student, index) => {
                  const attempt = attemptsData[student.enrollment_id];
                  const isSubmitted = !!attempt;

                  return (
                    <tr
                      key={student.user_id || index}
                      className="bg-white hover:bg-gray-50"
                    >
                      <td className="px-4 py-2.5 border border-slate-200">
                        <div className="flex items-center gap-2">
                          {student.avatar_url ? (
                            <img
                              src={student.avatar_url}
                              alt={student.full_name}
                              className="w-6 h-6 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-6 h-6 rounded-full bg-cyan-100 flex items-center justify-center text-xs font-semibold text-cyan-600">
                              {student.full_name?.charAt(0)?.toUpperCase() ||
                                "S"}
                            </div>
                          )}
                          <span className="text-sm text-zinc-800 font-['Roboto']">
                            {student.full_name}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-sm text-zinc-800 font-['Roboto'] border border-slate-200">
                        {student.user_id}
                      </td>
                      <td className="px-4 py-2.5 text-sm text-zinc-800 font-['Roboto'] border border-slate-200">
                        {attempt?.submitted_at
                          ? new Date(attempt.submitted_at).toLocaleDateString(
                              "en-US",
                              {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              },
                            )
                          : "--"}
                      </td>
                      <td className="px-4 py-2.5 border border-slate-200">
                        <span
                          className={`inline-block px-3 py-1 rounded-full text-xs text-white font-['Roboto'] ${
                            isSubmitted ? "bg-green-600" : "bg-yellow-500"
                          }`}
                        >
                          {isSubmitted ? "Submitted" : "Pending"}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-sm text-zinc-800 font-['Roboto'] border border-slate-200">
                        {attempt?.score ?? "--"}
                      </td>
                      <td className="px-4 py-2.5 border border-slate-200">
                        {attempt ? (
                          <span
                            className={`inline-block px-3 py-1 rounded-full text-xs text-white font-['Roboto'] ${
                              attempt.passed ? "bg-green-600" : "bg-red-600"
                            }`}
                          >
                            {attempt.passed ? "Pass" : "Fail"}
                          </span>
                        ) : (
                          <span className="text-sm text-zinc-800 font-['Roboto']">
                            --
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No students enrolled in this batch
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
